import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import cameraAngleFileApi from '@services/cameraAngleFileApi';
import AlertModal from '@components/common/AlertModal';
import ConfirmModal from '@components/common/ConfirmModal';

const CameraAngleFileManager = forwardRef(({ angleId, onFilesChange, isEditing = false, refreshTrigger }, ref) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // 파일 목록 조회
  const fetchFiles = async () => {
    if (!angleId) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await cameraAngleFileApi.getFiles(angleId);
      
      if (response.success) {
        setFiles(response.data || []);
        if (onFilesChange) {
          onFilesChange(response.data || []);
        }
      } else {
        console.error('파일 목록 조회 실패:', response.message);
        setAlertMessage(response.message || '파일 목록을 불러오는데 실패했습니다.');
        setShowAlertModal(true);
      }
    } catch (error) {
      console.error('파일 목록 조회 실패:', error);
      setAlertMessage('파일 목록을 불러오는데 실패했습니다.');
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  // 부모 컴포넌트에서 접근 가능한 메서드 노출
  useImperativeHandle(ref, () => ({
    refreshFiles: fetchFiles,
    getFiles: () => files
  }));

  useEffect(() => {
    fetchFiles();
  }, [angleId]);

  // refreshTrigger가 변경될 때마다 파일 목록 새로고침
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchFiles();
    }
  }, [refreshTrigger]);

  // 파일 순서 변경 (위로/아래로)
  const handleMoveFile = async (fileIndex, direction) => {
    const newFiles = [...files];
    const targetIndex = direction === 'up' ? fileIndex - 1 : fileIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= newFiles.length) return;

    // 배열에서 위치 변경
    [newFiles[fileIndex], newFiles[targetIndex]] = [newFiles[targetIndex], newFiles[fileIndex]];
    
    // displayOrder 업데이트
    const updatedFiles = newFiles.map((file, index) => ({
      ...file,
      displayOrder: index + 1,
    }));

    setFiles(updatedFiles);

    // API 호출로 순서 저장
    const fileOrders = updatedFiles.map((file) => ({
      fileId: file.id,
      displayOrder: file.displayOrder,
    }));

    try {
      const response = await cameraAngleFileApi.reorderFiles(angleId, fileOrders);
      if (!response.success) {
        setAlertMessage(response.message || '파일 순서 변경에 실패했습니다.');
        setShowAlertModal(true);
        // 실패 시 원래 상태로 복구
        fetchFiles();
      }
    } catch (error) {
      console.error('파일 순서 변경 실패:', error);
      setAlertMessage('파일 순서 변경에 실패했습니다.');
      setShowAlertModal(true);
      fetchFiles();
    }
  };

  // 파일 삭제
  const handleDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      const response = await cameraAngleFileApi.deleteFile(angleId, fileToDelete.id);
      if (response.success) {
        await fetchFiles();
        setAlertMessage(response.message || '파일이 삭제되었습니다.');
        setShowAlertModal(true);
      } else {
        setAlertMessage(response.message || '파일 삭제에 실패했습니다.');
        setShowAlertModal(true);
      }
    } catch (error) {
      console.error('파일 삭제 실패:', error);
      setAlertMessage('파일 삭제에 실패했습니다.');
      setShowAlertModal(true);
    } finally {
      setShowDeleteConfirm(false);
      setFileToDelete(null);
    }
  };


  // 삭제 확인 모달 열기
  const confirmDelete = (file) => {
    setFileToDelete(file);
    setShowDeleteConfirm(true);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>파일 목록을 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="camera-angle-file-manager">
      <div className="file-list-header">
        <h3 className="file-list-title">
          앵글 파일 목록 {files.length > 0 && <span className="file-count">({files.length}개)</span>}
        </h3>
        {isEditing && (
          <button
            className="btn-add-file"
            onClick={() => {
              const fileInput = document.getElementById('direct-file-input');
              if (fileInput) {
                fileInput.click();
              }
            }}
          >
            파일 추가
          </button>
        )}
      </div>

      {files.length === 0 ? (
        <div className="empty-state">
          <p>등록된 앵글 파일이 없습니다.</p>
          {isEditing && <p className="hint">상단의 파일 추가 버튼을 눌러 파일을 등록하세요.</p>}
        </div>
      ) : (
        <div className="file-list">
          <AnimatePresence>
            {files.map((file, index) => (
              <motion.div
                key={file.id}
                className="file-item"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.2 }}
              >
                <div className="file-order">
                  <span className="order-number">{file.displayOrder}</span>
                  {isEditing && files.length > 1 && (
                    <div className="order-controls">
                      <button
                        className="btn-order"
                        onClick={() => handleMoveFile(index, 'up')}
                        disabled={index === 0}
                        title="위로 이동"
                      >
                        ▲
                      </button>
                      <button
                        className="btn-order"
                        onClick={() => handleMoveFile(index, 'down')}
                        disabled={index === files.length - 1}
                        title="아래로 이동"
                      >
                        ▼
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="file-info">
                  <div className="file-name">{file.fileName}</div>
                  {file.originalFileName && (
                    <div className="original-name">{file.originalFileName}</div>
                  )}
                  <div className="file-meta">
                    <span className="file-id">ID: {file.fileResourceId}</span>
                  </div>
                </div>

                {isEditing && (
                  <div className="file-actions">
                    <button
                      className="btn-delete"
                      onClick={() => confirmDelete(file)}
                      title="삭제"
                    >
                      삭제
                    </button>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* 알림 모달 */}
      <AlertModal
        show={showAlertModal}
        message={alertMessage}
        onConfirm={() => setShowAlertModal(false)}
      />

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        message={`"${fileToDelete?.fileName}" 파일을 삭제하시겠습니까?`}
        onConfirm={handleDeleteFile}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setFileToDelete(null);
        }}
      />

      <style jsx="true">{`
        .camera-angle-file-manager {
          margin-top: 20px;
        }

        .loading-container {
          text-align: center;
          padding: 40px;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #4caf50;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-container p {
          color: #666;
        }

        .file-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          padding-bottom: 10px;
          border-bottom: 1px solid #e0e0e0;
        }

        .file-list-title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin: 0;
        }

        .file-count {
          font-size: 14px;
          color: #666;
          font-weight: normal;
          margin-left: 5px;
        }
        
        .btn-add-file {
          background: #4caf50;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-add-file:hover {
          background: #45a049;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #999;
        }

        .empty-state p {
          margin: 5px 0;
        }

        .hint {
          font-size: 14px;
          color: #666;
        }

        .file-list {
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          overflow: hidden;
        }

        .file-item {
          display: flex;
          align-items: center;
          padding: 15px;
          background: white;
          border-bottom: 1px solid #f0f0f0;
          transition: background-color 0.2s;
        }

        .file-item:last-child {
          border-bottom: none;
        }

        .file-item:hover {
          background-color: #f8f9fa;
        }

        .file-item.inactive {
          opacity: 0.6;
          background-color: #f5f5f5;
        }

        .file-order {
          display: flex;
          align-items: center;
          margin-right: 15px;
          gap: 10px;
        }

        .order-number {
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f0f0f0;
          border-radius: 50%;
          font-size: 14px;
          font-weight: 600;
          color: #666;
        }

        .order-controls {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .btn-order {
          width: 20px;
          height: 16px;
          background: #e0e0e0;
          border: none;
          border-radius: 2px;
          cursor: pointer;
          font-size: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
        }

        .btn-order:hover:not(:disabled) {
          background: #d0d0d0;
        }

        .btn-order:disabled {
          background: #f5f5f5;
          color: #ccc;
          cursor: not-allowed;
        }

        .file-info {
          flex: 1;
        }

        .file-name {
          font-weight: 500;
          color: #333;
          margin-bottom: 4px;
        }

        .original-name {
          font-size: 13px;
          color: #666;
          margin-bottom: 4px;
        }

        .file-meta {
          display: flex;
          gap: 10px;
          font-size: 12px;
          color: #999;
        }

        .file-id {
          font-family: monospace;
        }

        .inactive-badge {
          background: #ff9800;
          color: white;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 11px;
        }

        .file-actions {
          display: flex;
          gap: 8px;
        }

        .btn-toggle,
        .btn-delete {
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          font-size: 13px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-delete {
          background: #f44336;
          color: white;
        }

        .btn-delete:hover {
          opacity: 0.9;
        }
      `}</style>
    </div>
  );
});

CameraAngleFileManager.displayName = 'CameraAngleFileManager';

export default CameraAngleFileManager;